import { type ChatHeaderProps, ChatPanel } from "@qw/chat";
import { MCPPlatform } from "@qw/qw-common";
import { useMemo } from "react";
import { useParams } from "react-router";
import { useInboxQueryStates } from "@/providers/inbox-query-states-provider";
import { useConversation } from "@/providers/use-conversation";

import { canCreateInboxChatSession } from "./can-create-inbox-chat-session";
import { ChatEmptyState } from "./chat-empty-state";
import { useChatResourceLinkHandlers } from "./use-chat-resource-link-handlers";

export interface ChatSessionMessagesProps {
  chatSessionId?: string;
  header?: ChatHeaderProps;
  toolFilterStorageKey: string;
}

export const ChatSessionMessages = ({ chatSessionId, header, toolFilterStorageKey }: ChatSessionMessagesProps) => {
  const { conversationId } = useInboxQueryStates();
  const { insuredId } = useParams();
  const { isCollaboratorReply, isOwnInbox } = useConversation();
  const resourceLinkHandlers = useChatResourceLinkHandlers();
  const chatSessionsFilter = useMemo(() => {
    if (conversationId) {
      return { metadata: [{ key: "inboxConversationId", value: conversationId }] };
    }

    return { metadata: [{ key: "insuredId", value: insuredId ?? "" }] };
  }, [conversationId, insuredId]);
  const defaultPromptInput = useMemo(
    () => ({
      conversationId,
      insuredId,
    }),
    [conversationId, insuredId]
  );

  return (
    <ChatPanel
      apiUrl={import.meta.env.VITE_REACT_APP_API_PATH}
      allowCreateChatSession={canCreateInboxChatSession({
        conversationId,
        isOwnInbox,
        canReplyAsCollaborator: isCollaboratorReply,
      })}
      chatSessionId={chatSessionId}
      chatSessionsFilter={chatSessionsFilter}
      className="bg-card"
      compactActivePlaybooks
      defaultPromptInput={defaultPromptInput}
      emptyComponent={<ChatEmptyState />}
      header={header}
      platform={MCPPlatform.INBOX}
      resourceLinkHandlers={resourceLinkHandlers}
      toolFilterStorageKey={toolFilterStorageKey}
    />
  );
};
